/** Car trips into the charging zone: flowmap.gl's curved arrows from each origin to its
 *  destination inside the zone, as the Network Editor draws trip flows, with the zone
 *  over the basemap and, when given, the hexagons under it, coloured by their trips. Far
 *  from the zone, nearby TAZs merge into one circle until zoomed in. A click on a circle
 *  or a hexagon keeps only its trips, on an arrow those from where it starts; a click on
 *  empty map shows all again. Read-only. */

import { useMemo, useRef, useState } from 'react';
import { DeckGL } from '@deck.gl/react';
import { Map } from 'react-map-gl';
import { PolygonLayer } from '@deck.gl/layers';
import { FlowmapLayer, PickingType } from '@flowmap.gl/layers';
import { LocationFilterMode } from '@flowmap.gl/data';
import { FLOW_SCHEME } from '../../utils/networkEditor';
import { REGION_VIEW, zoneLayer, bandLayers } from './WorkshopMap';
import { useReleaseWebGL } from './webgl';
import { EMPTY, EDGE, PICKED_FILL, PICKED_EDGE, HIGHLIGHT } from './HexMap';
import { tazOf } from './flows';

// The whole Bay Area: far from the zone, TAZs merge into fewer, readable arrows.
const FLOWS_VIEW = { ...REGION_VIEW, longitude: -122.25, latitude: 37.74, zoom: 8.8 };
// Lighter than the hexagon page's: the arrows are drawn over them.
const HEX_ALPHA = 140;
const int = (n) => Math.round(n).toLocaleString('en-US');
const placeName = (l) => (l && l.children
  ? l.name
  : `TAZ ${l && l.taz ? l.taz : tazOf(l && l.id)}${l && l.inside ? ', inside the zone' : ''}`);

function describe(object) {
  if (object.type === PickingType.FLOW) {
    return `${placeName(object.origin)} → ${placeName(object.dest)}\n${int(object.count)} trips`
      + `\nClick to keep only the trips from ${placeName(object.origin)}`;
  }
  const t = object.totals || {};
  const into = (t.incomingCount || 0) > (t.outgoingCount || 0);
  return `${placeName(object.location)}\n`
    + (into ? `${int(t.incomingCount)} trips end here` : `${int(t.outgoingCount || 0)} trips into the zone start here`)
    + '\nClick to keep only them';
}

/** `selected`: { name, locations, leaves } of the TAZ or circle chosen: the arrows kept
 *  are those of `locations` (flowmap location ids), `leaves` the TAZ locations it stands
 *  for. `onSelect` takes a state updater for the circle clicked, { id, name, leaves,
 *  cluster }. `hexes`: { cells, values, colorOf, picked } draws the hexagons, `picked` a
 *  Set of hexagon ids; `describeHex(cell)` gives a hexagon's tooltip lines and
 *  `onPickHex(cell)` takes a click on one. `bands`: the protected neighbourhoods to draw,
 *  useBands' { outlines }. */
export default function FlowMap({ zone, data, selected, onSelect, fade, hexes, describeHex, onPickHex, bands, legend, children }) {
  const host = useRef(null);
  useReleaseWebGL(host);
  const [flowTip, setFlowTip] = useState(null);
  // The hexagon under the pointer; its text is written on each render, so it follows a click.
  const [hexTip, setHexTip] = useState(null);
  // Read on click only, so a new one does not rebuild the hexagon layer.
  const pickHex = useRef(null);
  pickHex.current = onPickHex;
  const locKey = selected && selected.locations.length ? selected.locations.join('\n') : '';
  const filter = useMemo(() => (locKey
    // locationFilterMode is required with selectedLocations: without it no flow passes.
    ? { selectedLocations: locKey.split('\n'), locationFilterMode: LocationFilterMode.ALL }
    : undefined), [locKey]);

  const hexLayer = useMemo(() => {
    if (!hexes) return null;
    const { values, colorOf, picked } = hexes;
    const isPicked = (c) => !!picked && picked.has(c.id);
    return new PolygonLayer({
      id: 'ws-flow-hexes',
      data: hexes.cells,
      getPolygon: (c) => c.polygon,
      filled: true,
      stroked: true,
      lineWidthUnits: 'pixels',
      getFillColor: (c) => {
        if (isPicked(c)) return PICKED_FILL;
        const v = values ? values.get(c.id) : undefined;
        return v === undefined ? EMPTY : [...colorOf(v), HEX_ALPHA];
      },
      getLineColor: (c) => (isPicked(c) ? PICKED_EDGE : EDGE),
      getLineWidth: (c) => (isPicked(c) ? 3 : 0.5),
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT,
      updateTriggers: {
        getFillColor: [picked, values, colorOf],
        getLineColor: [picked],
        getLineWidth: [picked],
      },
      onHover: (info) => setHexTip(info.object ? { x: info.x, y: info.y, cell: info.object } : null),
      onClick: (info) => { if (info.object) pickHex.current(info.object); },
    });
  }, [hexes]);

  const flowLayer = useMemo(() => (data && data.flows.length
    ? new FlowmapLayer({
      id: 'ws-flows',
      data,
      pickable: true,
      darkMode: true,
      colorScheme: FLOW_SCHEME,
      clusteringEnabled: true,
      clusteringAuto: true,
      fadeEnabled: true,
      fadeOpacityEnabled: true,
      fadeAmount: fade,
      flowLinesRenderingMode: 'curved',
      locationsEnabled: true,
      locationTotalsEnabled: true,
      highlightColor: '#7CC644',
      getLocationId: (l) => l.id,
      getLocationName: (l) => placeName(l),
      // Leaves come heaviest first.
      getLocationClusterName: (ids) => `${ids.length} TAZs around TAZ ${tazOf(ids[0])}`,
      getLocationLat: (l) => l.lat,
      getLocationLon: (l) => l.lon,
      getFlowOriginId: (f) => f.origin,
      getFlowDestId: (f) => f.dest,
      getFlowMagnitude: (f) => f.count,
      filter,
      // flowmap.gl resolves what is under the pointer only for its own handlers.
      onHover: (info) => setFlowTip(info && info.object ? { x: info.x, y: info.y, text: describe(info.object) } : null),
      onClick: (info) => {
        const obj = info && info.object;
        // A circle selects its TAZ, or the TAZs merged into it; an arrow the one it starts from.
        const location = !obj ? null
          : obj.type === PickingType.LOCATION ? obj.location
          : obj.type === PickingType.FLOW ? obj.origin : null;
        if (!location) return;
        const id = String(location.id);
        let leaves = [id];
        if (location.children) {
          const provider = info.layer && info.layer.state && info.layer.state.dataProvider;
          const index = provider && provider.getClusterIndex();
          const cluster = index && index.getClusterById(location.id);
          leaves = cluster ? index.expandCluster(cluster).map(String) : [];
        }
        const name = placeName(location);
        onSelect((cur) => (cur && cur.id === id ? null : { id, name, leaves, cluster: leaves.length !== 1 || leaves[0] !== id }));
      },
    })
    : null), [data, fade, filter, onSelect]);

  const zoneParts = useMemo(() => [zoneLayer('ws-flow', zone, 3, 'fill'), zoneLayer('ws-flow', zone, 3, 'line')], [zone]);
  const outlines = bands ? bands.outlines : null;
  const bandParts = useMemo(() => bandLayers('ws-flow', outlines), [outlines]);
  // The outline goes over the arrows, the fill under them: arrows hide the boundary. The
  // protected neighbourhoods' fill goes over the arrows and under the zone's outline,
  // their outline on top.
  const layers = useMemo(
    () => [hexLayer, zoneParts[0], flowLayer, bandParts[0], zoneParts[1], bandParts[1]].filter(Boolean),
    [hexLayer, zoneParts, flowLayer, bandParts],
  );
  const tip = (flowLayer && flowTip)
    || (hexLayer && hexTip && { x: hexTip.x, y: hexTip.y, text: describeHex(hexTip.cell).join('\n') })
    || null;

  return (
    <div className="ws-map" ref={host}>
      <DeckGL
        initialViewState={FLOWS_VIEW}
        controller={true}
        layers={layers}
        pickingRadius={4}
        onHover={(info) => { if (!info.layer) { setFlowTip(null); setHexTip(null); } }}
        onClick={(info) => { if (!info.layer) onSelect(null); }}
      >
        <Map
          mapStyle="mapbox://styles/mapbox/dark-v10"
          customAttribution='<a href="https://511.org" target="_blank" rel="noopener noreferrer">Bay Area transit: data provided by 511.org</a>'
        />
      </DeckGL>
      {tip && <div className="ws-flow-tip" style={{ left: tip.x + 12, top: tip.y + 12 }}>{tip.text}</div>}
      {children}
      {legend}
    </div>
  );
}
